import { Injectable } from '@angular/core';
import { FirebasePerformance } from '@capacitor-firebase/performance';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class PerformanceService {

  private isNative = false;
  private activeTraces: Map<string, boolean> = new Map();

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
  }

  /**
   * Activar/desactivar manualmente la recopilación de datos de rendimiento, p. está deshabilitado al iniciar la aplicación.
   * 
   * Parameters:
   * @param {boolean} enabled: habilitar o deshabilitar la recopilación de datos de rendimiento
   */
  public setPerformanceCollectionEnabled = async (enabled: boolean) => {
    if (!this.isNative) {
      return false;
    }

    try {
      await FirebasePerformance.setEnabled({ enabled });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Iniciar un seguimiento.
   * 
   * Parameters:
   * @param {string} name - nombre de la traza para comenzar.
   * @param {function} success - (optional) callback function para iniciar correctamente el seguimiento.
   * @param {function} error - (optional) callback function a la que se le pasará un mensaje de error {string} como argumento.
   */
  public startTrace = async (name: string, success?: (value: any) => any, error?: (value: any) => any) => {
    if (!this.isNative) {
      return false;
    }
    try {
      await FirebasePerformance.startTrace({ traceName: name });
      this.activeTraces.set(name, true);
      if (success) success(true);
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * Para contar los eventos relacionados con el rendimiento que ocurren en su aplicación (como aciertos o reintentos de caché),
   * agregue una línea de código similar a la siguiente cada vez que ocurra el evento,
   * usando una cadena que no sea reintentar para nombrar ese evento si está contando un diferente tipo de evento:
   * 
   * Parameters:
   * @param {string} name - nombre de la traza.
   * @param {function} success - (optional) callback function para llamar al contador que se incrementa con éxito.
   * @param {function} error - (optional) callback function a la que se le pasará un mensaje de error {string} como argumento.
   */
  public incrementCounter = async (name: string, success?: (value: any) => any, error?: (value: any) => any) => {
    if (!this.isNative) {
      return false;
    }
    try {
      // En Capacitor, necesitamos extraer el nombre de la traza y el nombre de la métrica
      // Ejemplo: si name es "myTrace_myMetric", traceName = "myTrace" y metricName = "myMetric"
      const parts = name.split('_');
      const traceName = parts[0];
      const metricName = parts.length > 1 ? parts.slice(1).join('_') : 'default_metric';

      await FirebasePerformance.incrementMetric({
        traceName,
        metricName,
        incrementBy: 1
      });
      if (success) success(true);
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * Detener el rastro
   * 
   * Parameters:
   * @param {string} name - nombre de la traza a detener.
   * @param {function} success - (optional) callback function para llamar a la detención exitosa del rastreo.
   * @param {function} error - (optional) callback function a la que se le pasará un mensaje de error {string} como argumento.
   */
  public stopTrace = async (name: string, success?: (value: any) => any, error?: (value: any) => any) => {
    if (!this.isNative) {
      return false;
    }
    try {
      if (this.activeTraces.get(name)) {
        await FirebasePerformance.stopTrace({ traceName: name });
        this.activeTraces.delete(name);
        if (success) success(true);
        return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  }
}
